  import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
  import { Observable, of, EMPTY } from 'rxjs';
  import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
  import { catchError, timeout, retry, finalize } from 'rxjs/operators';
  import { API_BASE, API_TOOL } from 'src/shared/common';

  interface LocalizedContent {
    vi: {
      title: string;
      short: string;
      version: string;
      change_lang: string;
      mccd: string;
      sex: string;
      age: string;
      age_unit1: string;
      partI: string;
      partI_desc: string;
      partII: string;
      partII_desc: string;
      warning: string;
      warning_desc: string;
      btn_run: string;
      conclusion: string;
      explanation: string;
      error_loading: string;
      error_connection: string;
      error_validation: string;
    };
    en: {
      title: string;
      short: string;
      version: string;
      change_lang: string;
      mccd: string;
      sex: string;
      age: string;
      age_unit1: string;
      partI: string;
      partI_desc: string;
      partII: string;
      partII_desc: string;
      warning: string;
      warning_desc: string;
      btn_run: string;
      conclusion: string;
      explanation: string;
      error_loading: string;
      error_connection: string;
      error_validation: string;
    };
  }

  interface ApiLanguageResponse {
    vi?: Partial<LocalizedContent['vi']>;
    en?: Partial<LocalizedContent['en']>;
    [key: string]: any;
  }

  type LanguageKey = 'vi' | 'en';

  @Component({
    selector: 'app-tools-ucodvn',
    templateUrl: './tools-ucod.component.html',
    styleUrls: ['./tools-ucod.component.scss']
  })
  export class ToolsUcodComponent implements OnInit {

    @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;
    isDragging = false;
    startX = 0;
    scrollLeft = 0;

    startDragging(event: MouseEvent) {
      this.isDragging = true;
      this.startX = event.pageX - this.scrollContainer.nativeElement.offsetLeft;
      this.scrollLeft = this.scrollContainer.nativeElement.scrollLeft;
    }

    onMouseMove(event: MouseEvent) {
      if (!this.isDragging) return;
      event.preventDefault();
      const x = event.pageX - this.scrollContainer.nativeElement.offsetLeft;
      const walk = (x - this.startX) * 1.5;
      this.scrollContainer.nativeElement.scrollLeft = this.scrollLeft - walk;
    }

    stopDragging() {
      this.isDragging = false;
    }

    isActive: boolean = true;
    isModalVisible = false;
    currentLanguage: LanguageKey = 'vi';
    isLoadingLanguage: boolean = false;
    isProcessing: boolean = false;
    apiError: string = '';

    content: LocalizedContent = {
      vi: {
        title: 'Công cụ UCOD',
        short: 'Mô tả ngắn',
        version: 'Phiên bản 1.0',
        change_lang: 'Đổi ngôn ngữ',
        mccd: 'MCCD',
        sex: 'Giới tính',
        age: 'Tuổi',
        age_unit1: 'Năm',
        partI: 'Phần I',
        partI_desc: 'Nguyên nhân trực tiếp dẫn đến tử vong',
        partII: 'Phần II',
        partII_desc: 'Các tình trạng khác góp phần vào tử vong',
        warning: 'Cảnh báo',
        warning_desc: 'Vui lòng kiểm tra thông tin trước khi gửi',
        btn_run: 'Phân tích',
        conclusion: '',
        explanation: '',
        error_loading: 'Lỗi tải dữ liệu ngôn ngữ',
        error_connection: 'Lỗi kết nối đến server',
        error_validation: 'Dữ liệu nhập vào không hợp lệ'
      },
      en: {
        title: 'UCOD Tool',
        short: 'Short Description',
        version: 'Version 1.0',
        change_lang: 'Change Language',
        mccd: 'MCCD',
        sex: 'Sex',
        age: 'Age',
        age_unit1: 'Years',
        partI: 'Part I',
        partI_desc: 'Direct cause of death',
        partII: 'Part II',
        partII_desc: 'Other conditions contributing to death',
        warning: 'Warning',
        warning_desc: 'Please check information before submitting',
        btn_run: 'Analyze',
        conclusion: '',
        explanation: '',
        error_loading: 'Error loading language data',
        error_connection: 'Connection error to server',
        error_validation: 'Invalid input data'
      }
    };

    tablesection: any;
    tablerela: any;
    table: any;
    result: any;
    sex: string = '';
    age: string = '';
    ageUnit: string = 'y';
    partI: string = '';
    partII: string = '';
    conclusion: string = '';
    explanation: string = '';
    
    diagnosisData: { code: string; name: string; part: string; line: number }[] = [];

    constructor(private http: HttpClient) {
      const savedLanguage = localStorage.getItem('ucod_language') as LanguageKey;
      if (savedLanguage && (savedLanguage === 'vi' || savedLanguage === 'en')) {
        this.currentLanguage = savedLanguage;
      }
      
      this.loadLanguageFromApi();
    }

    ngOnInit() {
      this.suppressChromeExtensionErrors();
    }

    loadLanguageFromApi(): void {
      this.isLoadingLanguage = true;
      this.apiError = '';
      
      const apiUrl = `${API_BASE}/api/language/ucod`;
      
      this.http.get<ApiLanguageResponse>(apiUrl)
        .pipe(
          timeout(10000),
          retry(2),
          catchError((error: HttpErrorResponse) => {
            console.warn('Language API not available, using default content:', error);
            this.handleLanguageApiError(error);
            return of(null);
          }),
          finalize(() => {
            this.isLoadingLanguage = false;
            this.updateLanguageContent();
          })
        )
        .subscribe({
          next: (response: ApiLanguageResponse | null) => {
            if (response) {
              this.updateContentFromApi(response);
            }
            this.updateLanguageContent();
          },
          error: (error) => {
            console.error('Unexpected error in language loading:', error);
          }
        });
    }

    private handleLanguageApiError(error: HttpErrorResponse): void {
      switch (error.status) {
        case 404:
          this.apiError = this.content[this.currentLanguage].error_loading;
          console.info('Language API endpoint not found. Using default language content.');
          break;
        case 0:
          this.apiError = this.content[this.currentLanguage].error_connection;
          console.warn('Network error while loading language data. Using defaults.');
          break;
        case 500:
          this.apiError = 'Server error. Using default language content.';
          break;
        default:
          this.apiError = 'Unable to load language preferences. Using defaults.';
      }
      
      setTimeout(() => {
        this.apiError = '';
      }, 5000);
    }

    private updateContentFromApi(apiResponse: ApiLanguageResponse): void {
      try {
        if (apiResponse.vi) {
          this.content.vi = { ...this.content.vi, ...apiResponse.vi };
        }
        if (apiResponse.en) {
          this.content.en = { ...this.content.en, ...apiResponse.en };
        }
      } catch (error) {
        console.error('Error updating content from API:', error);
      }
    }

    getLocalizedContent(key: keyof LocalizedContent['vi']): string {
      try {
        return this.content[this.currentLanguage][key] || this.content['vi'][key] || '';
      } catch (error) {
        console.error('Error getting localized content:', error);
        return '';
      }
    }

    getCurrentConclusion(): string {
      return this.conclusion || this.getLocalizedContent('conclusion');
    }

    getCurrentExplanation(): string {
      return this.explanation || this.getLocalizedContent('explanation');
    }

    private suppressChromeExtensionErrors(): void {
      const originalError = console.error;
      console.error = (...args: any[]) => {
        const message = args.join(' ');
        if (message.includes('Could not establish connection') || 
            message.includes('Receiving end does not exist')) {
          return;
        }
        originalError.apply(console, args);
      };
    }

    toggleActive(): void {
      this.isActive = !this.isActive;
    }

    openModal() {
      this.isModalVisible = true;
    }

    closeModal() {
      this.isModalVisible = false;
    }

    toggleLanguage() {
      const newLanguage: LanguageKey = this.currentLanguage === 'vi' ? 'en' : 'vi';
      
      localStorage.setItem('ucod_language', newLanguage);
      
      const confirmMessage = this.currentLanguage === 'vi' 
        ? 'Trang sẽ được tải lại và tất cả dữ liệu đã nhập sẽ bị xóa. Bạn có muốn tiếp tục?'
        : 'The page will be reloaded and all entered data will be cleared. Do you want to continue?';
      
      if (confirm(confirmMessage)) {
        this.clearAllFormData();
        
        window.location.reload();
      }
    }

    private clearAllFormData(): void {
      this.sex = '';
      this.age = '';
      this.ageUnit = 'y';
      this.partI = '';
      this.partII = '';
      this.conclusion = '';
      this.explanation = '';
      this.diagnosisData = [];
      this.table = null;
      this.tablerela = null;
      this.tablesection = null;
      this.result = null;
    }

    updateLanguageContent() {
      if (!this.conclusion) {
        this.conclusion = this.content[this.currentLanguage].conclusion;
      }
      if (!this.explanation) {
        this.explanation = this.content[this.currentLanguage].explanation;
      }
    }

    updateDiagnosisData() {
      this.diagnosisData = [];
      this.processCodes(this.partI.split('\n'), 'I');
      this.processCodes(this.partII.split(','), 'II');
    }

    private processCodes(codesArray: string[], part: string) {
      codesArray.forEach((code, index) => {
        const trimmedCode = code.trim();
        if (trimmedCode) {
          this.diagnosisData.push({
            code: trimmedCode,
            name: '',
            part,
            line: index + 1
          });
        }
      });
    }

    onCodesIChange() {
      this.updateDiagnosisData();
      this.onInputchange();
    }

    onCodesIIChange() {
      this.updateDiagnosisData();
      this.onInputchange();
    }

    onSubmit() {
      if (this.isProcessing) return;
      
      this.isProcessing = true;
      const formData = new FormData();
      const sex = this.sex === 'Nam' ? 'm' : 'f';
      const age = this.age || '9';
      const ageUnit = this.ageUnit === 'Năm' ? 'y' : 'm';

      formData.append('partI', this.partI.trim());
      formData.append('partII', this.partII.trim());
      formData.append('age', age);
      formData.append('ageUnit', ageUnit);
      formData.append('sex', sex);
      formData.append('lang', this.currentLanguage.toUpperCase());

      const validation = this.isValidInput(formData);
      if (!validation.isValid) {
        alert(validation.messages.join('\n'));
        this.isProcessing = false;
        return;
      }

      this.http.post(API_TOOL, formData)
        .pipe(
          timeout(30000),
          catchError((error: HttpErrorResponse) => this.handleApiError(error)),
          finalize(() => {
            this.isProcessing = false;
          })
        )
        .subscribe({
          next: (response: any) => {
            if (response) {
              this.conclusion = response?.result || '';
              this.explanation = response?.table || '';
              this.table = response.log;
              this.tablerela = response.matrix;
              this.tablesection = response.desc;
            }
          },
          error: (error) => {
            console.error('Submit error:', error);
          }
        });
    }

    onInputchange() {
      if (this.isProcessing) return;
      
      const formData = new FormData();
      const sex = this.sex === 'Nam' ? 'm' : 'f';
      const ageUnit = this.ageUnit === 'Năm' ? 'y' : 'm';

      formData.append('text1', this.partI.trim());
      formData.append('text2', this.partII.trim());
      formData.append('age', this.age + '');
      formData.append('age_unit', ageUnit);
      formData.append('sex', sex);
      formData.append('lang', this.currentLanguage.toUpperCase());

      this.http.post(`${API_BASE}/api/ucod/liveucod?lang=${this.currentLanguage}`, formData)
        .pipe(
          timeout(15000),
          catchError((error: HttpErrorResponse) => {
            console.warn('Live UCOD API error:', error);
            return EMPTY;
          })
        )
        .subscribe({
          next: (response: any) => {
            if (response) {
              this.conclusion = response?.result || '';
              this.explanation = response?.table || '';
              this.table = response.log;
              this.tablerela = response.matrix;
              this.tablesection = response.desc;
            }
          },
          error: (error) => {
            console.error('Input change error:', error);
          }
        });
    }
    
    public isValidInput(data: FormData): { isValid: boolean; messages: string[] } {
      const messages: string[] = [];
      const partI = data.get('partI') as string;
      
      if (!partI || !partI.trim()) {
        messages.push(
          this.currentLanguage === 'vi' 
            ? 'Phần I không được để trống.' 
            : 'Part I cannot be empty.'
        );
      }
      
      return { isValid: messages.length === 0, messages };
    }

    private handleApiError(error: HttpErrorResponse): Observable<never> {
      let errorMessage = this.currentLanguage === 'vi' 
        ? 'Có lỗi xảy ra trong quá trình kết nối.' 
        : 'A connection error occurred.';

      switch (error.status) {
        case 404:
          errorMessage = this.currentLanguage === 'vi'
            ? 'Không tìm thấy dịch vụ. Vui lòng thử lại sau.'
            : 'Service not found. Please try again later.';
          break;
        case 500:
          errorMessage = this.currentLanguage === 'vi'
            ? 'Lỗi server. Vui lòng thử lại sau.'
            : 'Server error. Please try again later.';
          break;
        case 0:
          errorMessage = this.currentLanguage === 'vi'
            ? 'Không thể kết nối đến server. Kiểm tra kết nối mạng.'
            : 'Cannot connect to server. Check your network connection.';
          break;
        default:
          if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }
      }

      alert(errorMessage);
      return EMPTY;
    }
shouldShowPatientInfo(): boolean {
  
  if (this.sex || this.age ) {
    return true;
  }
  
 
   return !!(this.partI?.trim()) || !!(this.partII?.trim());
  
}


hasInputData(): boolean {
  return this.shouldShowPatientInfo();
}
    
  }